import React from 'react';
import styled from 'styled-components';
import { IItemSourceGuesserItemAiResult } from '../../../types/itemSourceGuesser';

type Props = { value?: IItemSourceGuesserItemAiResult };

const ItemSourceGuesserAiResultRenderer: React.FC<Props> = (params) => {
  const value = params.value;
  if (!value || typeof value !== 'object') return null;

  const decision = value.decision;
  const overallConfidence = value.confidence;
  const reasoning = value.reasoning;
  const errorMessage = value.error;
  const suggestionsRaw: { source: string; confidence: number; will_be_applied?: boolean }[] = Array.isArray(
    value.suggested_sources,
  )
    ? value.suggested_sources
    : [];

  // Order suggestions by confidence desc
  const suggestions = suggestionsRaw
    .slice()
    .sort((a, b) => (b?.confidence ?? 0) - (a?.confidence ?? 0))
    .slice(0, 3);

  const confidencePercent = (n?: number) => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '');

  // Decision colors similar to CLValidator style system
  const decisionColor =
    decision === 'keep'
      ? '#4caf50'
      : decision === 'change'
      ? '#1565c0'
      : decision === 'accept'
      ? '#8E24AA'
      : decision === 'no_decision'
      ? '#9E9E9E'
      : decision === 'error'
      ? '#d32f2f'
      : '#555';

  // Build decision text
  const decisionLabel =
    decision === 'keep'
      ? 'Keep'
      : decision === 'change'
      ? 'Change'
      : decision === 'accept'
      ? 'Accept'
      : decision === 'no_decision'
      ? 'No Decision'
      : decision === 'error'
      ? 'Error'
      : undefined;


  // Special handling for error decision
  if (decision === 'error') {
    return (
      <ResultContainer>
        <DecisionHeader>
          <DecisionText $color={decisionColor}>Error</DecisionText>
        </DecisionHeader>
        {errorMessage && (
          <div style={{ color: '#d32f2f', marginTop: 4 }} title={errorMessage}>
            <i className="fa fa-exclamation-triangle" style={{ marginRight: 4 }} />
            {errorMessage}
          </div>
        )}
      </ResultContainer>
    );
  }

  return (
    <ResultContainer>
      {decisionLabel && (
        <DecisionHeader>
          <DecisionText $color={decisionColor}>{decisionLabel}</DecisionText>
          {typeof overallConfidence === 'number' && (
            <ConfidenceBadge $confidence={overallConfidence}>{confidencePercent(overallConfidence)}</ConfidenceBadge>
          )}
        </DecisionHeader>
      )}

      {suggestions.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {suggestions.map((s, idx) => {
            // Use the will_be_applied flag from backend
            const willBeApplied = s.will_be_applied === true;

            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <SuggestionText $willBeApplied={willBeApplied} $isFirst={idx === 0}>
                  {s.source || '—'}
                </SuggestionText>
                <ConfidenceBadge $confidence={s.confidence}>{confidencePercent(s.confidence)}</ConfidenceBadge>
              </div>
            );
          })}
        </div>
      ) : (
        <span style={{ color: '#777' }}>No suggestions</span>
      )}

      {reasoning && (
        <div
          style={{ color: '#666', lineHeight: 1.4, whiteSpace: 'normal', marginTop: 2 }}
        >
          {reasoning}
        </div>
      )}
    </ResultContainer>
  );
};

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 1px 0;
`;

const DecisionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
`;

const DecisionText = styled.span<{ $color: string }>`
  color: ${(props) => props.$color};
  font-weight: 600;
  white-space: nowrap;
`;

const ConfidenceBadge = styled.span<{ $confidence: number }>`
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  line-height: 1.4;
  background-color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#e8f5e9';
    if (conf >= 0.6) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#2e7d32';
    if (conf >= 0.6) return '#ef6c00';
    return '#c62828';
  }};
  border: 1px solid
    ${(props) => {
      const conf = props.$confidence;
      if (conf >= 0.8) return '#a5d6a7';
      if (conf >= 0.6) return '#ffcc80';
      return '#ef9a9a';
    }};
`;

const SuggestionText = styled.span<{ $willBeApplied: boolean; $isFirst: boolean }>`
  line-height: 1.3;
  color: ${(props) => (props.$willBeApplied || props.$isFirst ? '#1976d2' : '#555')};
`;

export default ItemSourceGuesserAiResultRenderer;
